import { useCallback, useMemo } from 'react';

import { LocationSort, LocationSortBy, SortOrder } from '@occc/graphql';
import { useSortingParams, SortingDirection, SortingValue } from '@occc-app/hooks/useSortingParams';

import {
  LOCATIONS_AFTER_PARAM,
  LOCATIONS_BEFORE_PARAM,
  LOCATIONS_SORT_DIRECTION_PARAM,
  LOCATIONS_SORT_FIELD_PARAM,
} from '../constants';

/**
 * Direction applied on the FIRST click of a column. Connection-time columns
 * start newest -> oldest (desc) per product; Site ID starts A -> Z (asc).
 */
const FIRST_CLICK_DIRECTION: Record<LocationSortBy, SortingDirection> = {
  [LocationSortBy.SiteId]: 'asc',
  [LocationSortBy.LastMobileConnection]: 'desc',
  [LocationSortBy.LastLoyaltyConnection]: 'desc',
};

const LOCATION_SORT_BY_VALUES = Object.values(LocationSortBy);

/** Narrows a raw URL-param value to a valid LocationSortBy without a cast. */
const isLocationSortBy = (field: string): field is LocationSortBy =>
  LOCATION_SORT_BY_VALUES.some((value) => value === field);

const mapDirectionToSortOrder: Record<SortingDirection, SortOrder> = {
  asc: SortOrder.Asc,
  desc: SortOrder.Desc,
};

const toLocationSort = (sortingValue: SortingValue | null): LocationSort | undefined => {
  if (!sortingValue) {
    return undefined;
  }

  const { field, direction } = sortingValue;

  if (!isLocationSortBy(field)) {
    return undefined;
  }

  return {
    field,
    direction: mapDirectionToSortOrder[direction],
  };
};

export type SiteManagementLocationsSorting = {
  /** GraphQL sort variable, or undefined when no column is sorted. */
  sort: LocationSort | undefined;
  /** Current UI sort state (field + direction), or null when unsorted. */
  sortingValue: SortingValue | null;
  /** Toggle/seed sorting for a column. Resets pagination to the first page. */
  toggleSorting: (field: LocationSortBy) => void;
};

export const useSiteManagementLocationsSorting = (): SiteManagementLocationsSorting => {
  const [sortingValue, setSortingValue] = useSortingParams({
    beforeParamName: LOCATIONS_BEFORE_PARAM,
    afterParamName: LOCATIONS_AFTER_PARAM,
    sortFieldParamName: LOCATIONS_SORT_FIELD_PARAM,
    sortDirectionParamName: LOCATIONS_SORT_DIRECTION_PARAM,
  });

  const toggleSorting = useCallback(
    (field: LocationSortBy) => {
      const direction: SortingDirection =
        sortingValue?.field === field
          ? sortingValue.direction === 'asc'
            ? 'desc'
            : 'asc'
          : FIRST_CLICK_DIRECTION[field];

      setSortingValue({ field, direction });
    },
    [sortingValue, setSortingValue],
  );

  const sort = useMemo(() => toLocationSort(sortingValue), [sortingValue]);

  return useMemo(
    () => ({ sort, sortingValue, toggleSorting }),
    [sort, sortingValue, toggleSorting],
  );
};
